if (!customElements.get('collection-list')) {
  class CollectionList extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        sliderWrapper: '.swiper-wrapper',
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      };

      this.classes = {
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
      };

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);
      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);
      this.cardStyle = this.dataset.cardStyle;
      this.itemSize = this.dataset.itemSize;
      this.columnsDesktop = this.dataset.columnsDesktop;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.sliderInstance = false;

      this.init();
    }

    init() {
      this.initSlider();
    }

    getOriginalSlides() {
      const wrapper = this.sliderWrapper || this.querySelector(this.selectors?.sliderWrapper || '.swiper-wrapper');
      if (!wrapper) return [];
      return Array.from(wrapper.querySelectorAll(':scope > .swiper-slide:not(.swiper-slide-duplicate)'));
    }

    getSlideRealIndex(slide) {
      if (!slide) return 0;

      const loopIndex = Number(slide.dataset.swiperSlideIndex);
      if (Number.isFinite(loopIndex)) return loopIndex;

      const fromDom = this.getOriginalSlides().indexOf(slide);
      if (fromDom >= 0) return fromDom;

      const fromData = Number(slide.dataset.index);
      return Number.isFinite(fromData) ? fromData : 0;
    }

    getActiveSlideIndex() {
      const activeCard = this.querySelector(
        '.swiper-slide:not(.swiper-slide-duplicate) .collection-card--active'
      );
      if (!activeCard) return 0;

      return this.getSlideRealIndex(activeCard.closest('.swiper-slide'));
    }

    initSlider() {
      const initialSlide = this.getActiveSlideIndex();

      const sliderOptions = {
        slidesPerView: 'auto',
        spaceBetween: 8,
        navigation: {
          nextEl: this.section.querySelector(this.selectors.nextEl),
          prevEl: this.section.querySelector(this.selectors.prevEl),
        },
        pagination: false,
        loop: true,
        loopAdditionalSlides: 3,
        centeredSlides: true,
        slideToClickedSlide: false,
        watchOverflow: true,
        threshold: 12,
        freeMode: false,
        initialSlide,
        speed: this.reduceMotion ? 0 : 300,
        breakpoints: {
          768: {
            spaceBetween: this.cardStyle == 'card' ? 8 : 16,
            slidesPerView: 'auto',
            centeredSlides: true,
          },
          1024: {
            spaceBetween: this.cardStyle == 'card' ? 8 : 16,
            slidesPerView: 'auto',
            centeredSlides: true,
          },
          1280: {
            spaceBetween: this.cardStyle == 'card' ? 8 : 20,
            slidesPerView: 'auto',
            centeredSlides: true,
          },
        },
        on: {
          init: (swiper) => {
            this.centerActiveCollection(swiper, 0);
            this.updateEdgeState(swiper);
          },
          slideChange: (swiper) => this.updateEdgeState(swiper),
          resize: (swiper) => {
            this.centerActiveCollection(swiper, 0);
            this.updateEdgeState(swiper);
          },
        },
      };

      if (typeof this.sliderInstance !== 'object') {
        this.classList.add(this.classes.swiper);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);

        this.sliderInstance = new window.FoxTheme.Carousel(this, sliderOptions);
        this.sliderInstance.init();

        requestAnimationFrame(() => {
          const slider = this.sliderInstance && this.sliderInstance.slider;
          if (!slider) return;
          this.centerActiveCollection(slider, 0);
          this.updateEdgeState(slider);
        });

        const focusableElements = FoxTheme.a11y.getFocusableElements(this);

        focusableElements.forEach((element) => {
          element.addEventListener('focusin', () => {
            const slide = element.closest('.swiper-slide');
            if (!slide || !this.sliderInstance.slider) return;
            this.sliderInstance.slider.slideToLoop(
              this.getSlideRealIndex(slide),
              this.reduceMotion ? 0 : undefined
            );
          });
        });

        if (Shopify.designMode) {
          document.addEventListener('shopify:block:select', (e) => {
            if (e.detail.sectionId != this.sectionId) return;
            let { target } = e;

            this.sliderInstance.slider.slideToLoop(
              this.getSlideRealIndex(target),
              this.reduceMotion ? 0 : undefined
            );
          });
        }
      }
    }

    updateEdgeState(swiper) {
      const slider = swiper || this.sliderInstance?.slider;
      if (!slider) return;

      const content = this.closest('.section__content');
      if (!content) return;

      if (slider.params.loop) {
        content.classList.remove('is-at-start', 'is-at-end');
        return;
      }

      content.classList.toggle('is-at-start', slider.isBeginning);
      content.classList.toggle('is-at-end', slider.isEnd);
    }

    centerActiveCollection(swiper, speed) {
      const slider = swiper || this.sliderInstance?.slider;
      if (!slider) return;

      const activeCard = this.querySelector('.collection-card--active');
      if (!activeCard) return;

      const slideIndex = this.getActiveSlideIndex();
      const duration = speed === 0 || this.reduceMotion ? 0 : speed;

      slider.slideToLoop(slideIndex, duration);
    }

    destroySlider() {
      this.classList.remove(this.classes.swiper);
      this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
      if (typeof this.sliderInstance !== 'object') return;
      this.sliderInstance.slider.destroy();
      this.sliderInstance = false;
    }
  }
  customElements.define('collection-list', CollectionList);
}
